import {
  PaginaPlanos,
  PlanoAlteracao,
  PlanoCadastro,
  PlanoDetalhe,
  PlanoResumo,
  ProdutoPlano,
} from '../../models/parametros/plano.model';
import { OpcoesInstitutosPlano } from '../../models/parametros/plano-institutos.model';
import { FiltroPlanos, produtoEncerrado } from '../../utils/parametros/planos.util';
import {
  ClonarPlanoParamCommandDto,
  CreatePlanoParamCommandDto,
  PatchPlanoParamCommandDto,
  PlanoParamResponseDto,
  PlanoProdutoResponseDto,
  PlanosParamsQueryParams,
  PlanosParamsResponseDto,
} from '../../models/parametros/plano-api.model';

/** Texto vazio e status `'todos'` ficam de fora da consulta. */
export function filtroPlanosToQueryParams(
  filtro: FiltroPlanos,
  pagina: number,
  tamanhoPagina: number,
): PlanosParamsQueryParams {
  const codigo = filtro.codigo.trim();
  const nome = filtro.nome.trim();

  return {
    ...(codigo ? { cod_plano: codigo } : {}),
    ...(nome ? { ds_plano: nome } : {}),
    ...(filtro.status !== 'todos' ? { ativo: filtro.status === 'Ativo' ? 'S' : 'N' } : {}),
    pagina,
    tamanho_pagina: tamanhoPagina,
  };
}

/** O código exibido é o COD_PLANO: o código do produto no Amadeus (NUM_PLBNF), que a API deriva. */
export function planoParamDtoToPlanoResumo(dto: PlanoParamResponseDto): PlanoResumo {
  return {
    id: dto.id_plano,
    codigo: dto.cod_plano?.trim() ?? '',
    nome: dto.ds_plano?.trim() ?? '',
    tipo: dto.tp_plano?.trim() ?? '',
    status: dto.fl_ativo ? 'Ativo' : 'Inativo',
    ultimaAtualizacao: (dto.dt_ult_atu ?? dto.dt_inclusao ?? '').slice(0, 10),
  };
}

export function planoParamDtoToPlanoDetalhe(dto: PlanoParamResponseDto): PlanoDetalhe {
  return {
    ...planoParamDtoToPlanoResumo(dto),
    descricao: dto.desc_param?.trim() ?? '',
    produto: {
      id: dto.id_produto ?? 0,
      descricao: dto.produto?.des_produto?.trim() ?? '',
      codigo: dto.cod_plano?.trim() ?? '',
      idPlanoParam: dto.id_plano,
      encerrado: produtoEncerrado(dto.produto?.dt_fim),
    },
  };
}

export function planosParamsResponseToPaginaPlanos(response: PlanosParamsResponseDto): PaginaPlanos {
  return {
    itens: (response.itens ?? []).map(planoParamDtoToPlanoResumo),
    totalRegistros: response.total_registros ?? 0,
    totalPaginas: response.total_paginas ?? 0,
  };
}

export function planoProdutoDtoToProdutoPlano(dto: PlanoProdutoResponseDto): ProdutoPlano {
  return {
    id: dto.id,
    descricao: dto.des_produto?.trim() ?? '',
    codigo: dto.cod_plano?.trim() ?? '',
    idPlanoParam: dto.id_plano_param ?? null,
    encerrado: produtoEncerrado(dto.dt_fim),
  };
}

/**
 * Plano novo não combina institutos até a aba "Instituto" ser configurada. COD_PLANO não é enviado (a API
 * usa o código do produto no Amadeus); TP_PLANO, DS_PRODUTO e DESC_PARAM também não (a API preenche).
 */
export function planoCadastroToCreateCommand(dados: PlanoCadastro): CreatePlanoParamCommandDto {
  return {
    id_produto: dados.produto.id,
    ds_plano: dados.nome.trim(),
    fl_permite_comb_inst: false,
    fl_ativo: dados.status === 'Ativo',
  };
}

export function planoAlteracaoToPatchCommand(dados: PlanoAlteracao): PatchPlanoParamCommandDto {
  return {
    ds_plano: dados.nome.trim(),
    desc_param: dados.descricao.trim(),
    fl_ativo: dados.status === 'Ativo',
  };
}

/** Prazo 0 (o default da API para plano novo) é prazo ainda não informado. */
export function planoParamDtoToOpcoesInstitutos(dto: PlanoParamResponseDto): OpcoesInstitutosPlano {
  const prazo = dto.nr_dias_prazo_opc ?? 0;
  return {
    prazoOpcaoDias: prazo > 0 ? prazo : null,
    permitirCombinacao: dto.fl_permite_comb_inst ?? false,
  };
}

export function opcoesInstitutosToPatchCommand(opcoes: OpcoesInstitutosPlano): PatchPlanoParamCommandDto {
  return {
    fl_permite_comb_inst: opcoes.permitirCombinacao,
    ...(opcoes.prazoOpcaoDias !== null ? { nr_dias_prazo_opc: opcoes.prazoOpcaoDias } : {}),
  };
}

/** A cópia vai para o produto destino e nasce inativa, com o nome informado; o resto vem da origem. */
export function clonagemToClonarCommand(destino: ProdutoPlano, nome: string): ClonarPlanoParamCommandDto {
  return { id_produto: destino.id, ds_plano: nome, fl_ativo: false };
}
